import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, GitBranch, ChevronDown, ChevronRight, ZoomIn, ZoomOut, Expand, Shrink, Calendar, AlertTriangle, Eye, EyeOff, Building2, Layers, Box } from 'lucide-react';
import { Task } from '../shared/types';
import { formatDate } from '../shared/utils';

// Critical path categories - these are always on the critical path
const CRITICAL_PATH_CATEGORIES = [
  'RCC',
  'Site Preparation',
  'Earthwork',
  'Waterproofing',
  'Scaffolding'
];

// Zoom level configurations
type ZoomLevel = 'month' | 'quarter' | 'half-year' | 'year';

const ZOOM_CONFIG: Record<ZoomLevel, { label: string; minColumnWidth: number; unitDays: number }> = {
  'month': { label: 'Monthly', minColumnWidth: 60, unitDays: 30 },
  'quarter': { label: 'Quarterly', minColumnWidth: 80, unitDays: 91 },
  'half-year': { label: 'Half Year', minColumnWidth: 100, unitDays: 182 },
  'year': { label: 'Yearly', minColumnWidth: 120, unitDays: 365 },
};

interface GanttChartViewProps {
  tasks: Task[];
  getDependencyInfo: (task: Task) => Task[];
}

// Floor ordering - matches database floors.level_order logic
const getFloorOrder = (floorName: string): number => {
  if (!floorName) return 999;
  const lower = floorName.toLowerCase();
  
  // Special floors (in construction sequence)
  if (lower.includes('basement') && lower.includes('2')) return -20;
  if (lower.includes('basement') && lower.includes('1')) return -10;
  if (lower.includes('basement') || lower.includes('bsmt')) return -10;
  if (lower.includes('retaining')) return -15;
  if (lower.includes('plinth')) return -5;
  if (lower.includes('ground') || lower === 'gf') return 0;
  if (lower.includes('podium') && lower.includes('1')) return 1;
  if (lower.includes('podium') && lower.includes('2')) return 2;
  if (lower.includes('podium') && lower.includes('3')) return 3;
  if (lower.includes('podium') && lower.includes('4')) return 4;
  if (lower.includes('podium')) return 1;
  if (lower.includes('terrace') || lower.includes('roof')) return 500;
  
  // Extract number from floor name (01st, 02nd, 1st, 2nd, 10th, etc.)
  const match = floorName.match(/(\d+)/);
  if (match) {
    return parseInt(match[1], 10) + 10; // Add 10 to put after special floors
  }
  
  return 999; // Unknown floors at end
};

// Stage ordering - matches database construction_stages.display_order
// Follows typical construction sequence
const STAGE_ORDER: Record<string, number> = {
  // Site & Foundation
  'Site Preparation': 1,
  'Demolition & Dismantling': 2,
  'Earthwork': 3,
  // Structure
  'RCC': 10,
  'Scaffolding': 15,
  // Masonry & MEP Rough-in
  'Masonry': 20,
  'Plumbing': 25,
  'Electrical': 30,
  'Waterproofing': 35,
  // Finishing - Interior
  'Plaster Internal': 40,
  'Flooring & Tiling': 45,
  'False Ceiling': 50,
  'Painting Internal': 55,
  // Finishing - Exterior
  'External Plaster': 60,
  'Painting External': 65,
  // Doors, Windows, Fixtures
  'Doors & Windows': 70,
  'Fabrication': 75,
  // Final
  'Services/Utilities': 80,
  'Painting': 85,
  // Admin/Other
  'Departmental': 90,
  'Overheads': 95,
};

const getStageOrder = (category: string): number => {
  return STAGE_ORDER[category] || 50;
};

// Get color for category
const getCategoryBarColor = (category: string): string => {
  const colors: Record<string, string> = {
    'Earthwork': 'bg-amber-600',
    'RCC': 'bg-red-500',
    'Masonry': 'bg-orange-500',
    'Plumbing': 'bg-blue-500',
    'Electrical': 'bg-yellow-500',
    'Waterproofing': 'bg-cyan-500',
    'Plaster Internal': 'bg-pink-400',
    'External Plaster': 'bg-pink-600',
    'Flooring & Tiling': 'bg-purple-500',
    'Painting Internal': 'bg-rose-400',
    'Painting External': 'bg-rose-600',
    'Doors & Windows': 'bg-indigo-500',
    'Fabrication': 'bg-slate-500',
    'Services/Utilities': 'bg-teal-500',
    'Site Preparation': 'bg-lime-600',
    'False Ceiling': 'bg-violet-500',
  };
  return colors[category] || 'bg-gray-500';
};

export const GanttChartView: React.FC<GanttChartViewProps> = ({ tasks }) => {
  // Start with categories collapsed by default (only show stage-level view)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [initializedExpansion, setInitializedExpansion] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
  const [hasScrolledToToday, setHasScrolledToToday] = useState(false);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [criticalPathOnly, setCriticalPathOnly] = useState(false);
  const [hoverTip, setHoverTip] = useState<{
    name: string;
    type: string;
    category?: string;
    minDate: Date;
    maxDate: Date;
    taskCount: number;
    completed: number;
    progress: number;
    dependsOnRCC?: boolean;
    dependsOnPrevFloor?: boolean;
    isCritical: boolean;
    x: number;
    y: number;
  } | null>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const monthHeaderRef = useRef<HTMLDivElement>(null);
  const CHART_BODY_HEIGHT = 'min(72vh, 820px)';

  // Sync scroll between panels (vertical: left/right, horizontal: header/timeline)
  const handleScroll = useCallback((source: 'left' | 'right') => {
    const leftPanel = leftPanelRef.current;
    const rightPanel = rightPanelRef.current;
    const monthHeader = monthHeaderRef.current;
    
    if (!leftPanel || !rightPanel) return;
    
    if (source === 'left') {
      rightPanel.scrollTop = leftPanel.scrollTop;
    } else {
      leftPanel.scrollTop = rightPanel.scrollTop;
      // Also sync horizontal scroll with month headers
      if (monthHeader) {
        monthHeader.scrollLeft = rightPanel.scrollLeft;
      }
    }
  }, []);

  // Group tasks by Building → Category → Floor (no unit level)
  const groupedData = useMemo(() => {
    const buildings: Record<string, {
      name: string;
      minDate: Date | null;
      maxDate: Date | null;
      taskCount: number;
      completed: number;
      inProgress: number;
      categories: Record<string, {
        name: string;
        order: number;
        minDate: Date | null;
        maxDate: Date | null;
        taskCount: number;
        completed: number;
        inProgress: number;
        floors: Record<string, {
          name: string;
          order: number;
          minDate: Date | null;
          maxDate: Date | null;
          taskCount: number;
          completed: number;
          inProgress: number;
        }>;
      }>;
    }> = {};

    tasks.forEach(task => {
      const building = task.building || 'Unknown';
      const category = task.category || 'Unknown';
      const floor = task.floor || 'N/A';
      
      const startDate = task.originalSchedule?.startDate || task.startDate;
      const endDate = task.originalSchedule?.finishDate || task.finishDate;
      const taskStart = startDate ? new Date(startDate) : null;
      const taskEnd = endDate ? new Date(endDate) : null;
      const isCompleted = task.status === 'Completed';
      const isInProgress = task.status === 'In Progress' || task.status === 'Started';

      // Initialize building
      if (!buildings[building]) {
        buildings[building] = {
          name: building,
          minDate: null,
          maxDate: null,
          taskCount: 0,
          completed: 0,
          inProgress: 0,
          categories: {}
        };
      }

      // Initialize category
      if (!buildings[building].categories[category]) {
        buildings[building].categories[category] = {
          name: category,
          order: getStageOrder(category),
          minDate: null,
          maxDate: null,
          taskCount: 0,
          completed: 0,
          inProgress: 0,
          floors: {}
        };
      }

      // Initialize floor
      if (!buildings[building].categories[category].floors[floor]) {
        buildings[building].categories[category].floors[floor] = {
          name: floor,
          order: getFloorOrder(floor),
          minDate: null,
          maxDate: null,
          taskCount: 0,
          completed: 0,
          inProgress: 0
        };
      }

      // Update counts at all levels
      buildings[building].taskCount++;
      buildings[building].categories[category].taskCount++;
      buildings[building].categories[category].floors[floor].taskCount++;
      
      if (isCompleted) {
        buildings[building].completed++;
        buildings[building].categories[category].completed++;
        buildings[building].categories[category].floors[floor].completed++;
      }
      if (isInProgress) {
        buildings[building].inProgress++;
        buildings[building].categories[category].inProgress++;
        buildings[building].categories[category].floors[floor].inProgress++;
      }

      // Update date ranges at all levels
      if (taskStart && !isNaN(taskStart.getTime())) {
        const floorData = buildings[building].categories[category].floors[floor];
        const catData = buildings[building].categories[category];
        const bldgData = buildings[building];

        // Floor level
        if (!floorData.minDate || taskStart < floorData.minDate) floorData.minDate = new Date(taskStart);
        if (taskEnd && (!floorData.maxDate || taskEnd > floorData.maxDate)) floorData.maxDate = new Date(taskEnd);

        // Category level
        if (!catData.minDate || taskStart < catData.minDate) catData.minDate = new Date(taskStart);
        if (taskEnd && (!catData.maxDate || taskEnd > catData.maxDate)) catData.maxDate = new Date(taskEnd);

        // Building level
        if (!bldgData.minDate || taskStart < bldgData.minDate) bldgData.minDate = new Date(taskStart);
        if (taskEnd && (!bldgData.maxDate || taskEnd > bldgData.maxDate)) bldgData.maxDate = new Date(taskEnd);
      }
    });

    return buildings;
  }, [tasks]);

  // Calculate overall date range and time periods based on zoom level
  const { minDate, maxDate, totalDays, timePeriods } = useMemo(() => {
    let min: Date | null = null;
    let max: Date | null = null;

    Object.values(groupedData).forEach(building => {
      if (building.minDate && (!min || building.minDate < min)) min = new Date(building.minDate);
      if (building.maxDate && (!max || building.maxDate > max)) max = new Date(building.maxDate);
    });

    if (!min || !max) {
      const now = new Date();
      return {
        minDate: now,
        maxDate: new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000),
        totalDays: 365,
        timePeriods: []
      };
    }

    // Add padding
    const minD = new Date(min);
    const maxD = new Date(max);
    minD.setDate(minD.getDate() - 14);
    maxD.setDate(maxD.getDate() + 30);

    const total = Math.ceil((maxD.getTime() - minD.getTime()) / (1000 * 60 * 60 * 24));

    // Generate time periods based on zoom level
    const periods: Array<{ label: string; days: number; startDate: Date }> = [];
    let currentDate = new Date(minD);

    const getQuarter = (month: number) => Math.floor(month / 3) + 1;
    const getHalfYear = (month: number) => month < 6 ? 'H1' : 'H2';

    while (currentDate <= maxD) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      if (zoomLevel === 'month') {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        periods.push({
          label: currentDate.toLocaleString('default', { month: 'short', year: '2-digit' }),
          days: daysInMonth,
          startDate: new Date(currentDate)
        });
        currentDate.setMonth(currentDate.getMonth() + 1);
      } else if (zoomLevel === 'quarter') {
        const quarter = getQuarter(month);
        const quarterStart = new Date(year, (quarter - 1) * 3, 1);
        const quarterEnd = new Date(year, quarter * 3, 0);
        const daysInQuarter = Math.ceil((quarterEnd.getTime() - quarterStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        // Check if we already added this quarter
        const quarterLabel = `Q${quarter} ${year.toString().slice(-2)}`;
        if (!periods.some(p => p.label === quarterLabel)) {
          periods.push({
            label: quarterLabel,
            days: daysInQuarter,
            startDate: quarterStart
          });
        }
        currentDate.setMonth(currentDate.getMonth() + 3);
      } else if (zoomLevel === 'half-year') {
        const half = getHalfYear(month);
        const halfStart = half === 'H1' ? new Date(year, 0, 1) : new Date(year, 6, 1);
        const halfEnd = half === 'H1' ? new Date(year, 5, 30) : new Date(year, 11, 31);
        const daysInHalf = Math.ceil((halfEnd.getTime() - halfStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        const halfLabel = `${half} ${year}`;
        if (!periods.some(p => p.label === halfLabel)) {
          periods.push({
            label: halfLabel,
            days: daysInHalf,
            startDate: halfStart
          });
        }
        currentDate.setMonth(currentDate.getMonth() + 6);
      } else if (zoomLevel === 'year') {
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);
        const daysInYear = Math.ceil((yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        const yearLabel = year.toString();
        if (!periods.some(p => p.label === yearLabel)) {
          periods.push({
            label: yearLabel,
            days: daysInYear,
            startDate: yearStart
          });
        }
        currentDate.setFullYear(currentDate.getFullYear() + 1);
      }
    }

    return { minDate: minD, maxDate: maxD, totalDays: total, timePeriods: periods };
  }, [groupedData, zoomLevel]);

  // Scroll to today's date position (defined after minDate/totalDays are available)
  const scrollToToday = useCallback(() => {
    const rightPanel = rightPanelRef.current;
    const monthHeader = monthHeaderRef.current;
    if (!rightPanel || !minDate || totalDays <= 0) return;

    try {
      const today = new Date();
      const daysFromStart = Math.ceil((today.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // Only scroll if today is within the timeline range
      if (daysFromStart < 0 || daysFromStart > totalDays) return;
      
      const scrollPosition = (daysFromStart / totalDays) * rightPanel.scrollWidth;
      
      // Scroll to a position where today is ~20% from left (not exactly at the edge)
      const offset = rightPanel.clientWidth * 0.2;
      const targetScroll = Math.max(0, scrollPosition - offset);
      
      rightPanel.scrollLeft = targetScroll;
      if (monthHeader) {
        monthHeader.scrollLeft = targetScroll;
      }
    } catch (e) {
      console.warn('Failed to scroll to today:', e);
    }
  }, [minDate, totalDays]);

  // Expand all categories to show floors (defined after groupedData is available)
  const expandAll = useCallback(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return;
    
    const newExpanded: Record<string, boolean> = {};
    Object.keys(groupedData).forEach(buildingName => {
      const building = groupedData[buildingName];
      if (!building?.categories) return;
      
      const buildingKey = `bldg-${buildingName}`;
      newExpanded[buildingKey] = true;
      Object.keys(building.categories).forEach(categoryName => {
        newExpanded[`${buildingKey}-cat-${categoryName}`] = true;
      });
    });
    setExpandedGroups(newExpanded);
  }, [groupedData]);

  // Collapse to stage-level view (only show categories, hide floors)
  const collapseToStageLevel = useCallback(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return;
    
    const newExpanded: Record<string, boolean> = {};
    Object.keys(groupedData).forEach(buildingName => {
      const building = groupedData[buildingName];
      if (!building?.categories) return;
      
      const buildingKey = `bldg-${buildingName}`;
      newExpanded[buildingKey] = true; // Keep buildings expanded
      Object.keys(building.categories).forEach(categoryName => {
        newExpanded[`${buildingKey}-cat-${categoryName}`] = false; // Collapse categories
      });
    });
    setExpandedGroups(newExpanded);
  }, [groupedData]);

  // Build flat list of rows: Building (header only) → Category → Floor (no unit)
  // Building is just a grouping header, not an item with a bar
  const rows = useMemo(() => {
    const result: Array<{
      type: 'building' | 'category' | 'floor';
      key: string;
      name: string;
      level: number;
      minDate: Date | null;
      maxDate: Date | null;
      taskCount: number;
      completed: number;
      inProgress: number;
      category?: string;
      dependsOnRCC?: boolean;
      dependsOnPrevFloor?: boolean;
      isHeader?: boolean; // Building rows are headers only, no bar
      isCriticalPath?: boolean; // Part of critical path chain
    }> = [];

    Object.values(groupedData)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(building => {
        const buildingKey = `bldg-${building.name}`;
        
        // Building row (header only - no bar displayed)
        result.push({
          type: 'building',
          key: buildingKey,
          name: building.name,
          level: 0,
          minDate: building.minDate,
          maxDate: building.maxDate,
          taskCount: building.taskCount,
          completed: building.completed,
          inProgress: building.inProgress,
          isHeader: true // Mark as header - no bar will be shown
        });

        // If building is expanded, show categories
        if (expandedGroups[buildingKey] !== false) {
          const sortedCategories = Object.values(building.categories)
            .sort((a, b) => a.order - b.order);

          const rccExists = sortedCategories.some(c => c.name === 'RCC');

          sortedCategories.forEach((category) => {
            const categoryKey = `${buildingKey}-cat-${category.name}`;
            const isRCC = category.name === 'RCC';
            
            // Determine if this category is on the critical path
            // Critical path includes: RCC, Site Preparation, Earthwork, and structural work
            const isCriticalCategory = CRITICAL_PATH_CATEGORIES.some(cp => 
              category.name.toLowerCase().includes(cp.toLowerCase())
            );
            
            // Category row
            result.push({
              type: 'category',
              key: categoryKey,
              name: category.name,
              level: 1,
              minDate: category.minDate,
              maxDate: category.maxDate,
              taskCount: category.taskCount,
              completed: category.completed,
              inProgress: category.inProgress,
              category: category.name,
              dependsOnRCC: !isRCC && rccExists,
              isCriticalPath: isCriticalCategory
            });

            // If category is expanded, show floors
            if (expandedGroups[categoryKey] !== false) {
              const sortedFloors = Object.values(category.floors)
                .filter(f => f.name.toLowerCase() !== 'building') // Filter out "Building" as a floor name
                .sort((a, b) => a.order - b.order);

              sortedFloors.forEach((floor, floorIdx) => {
                const floorKey = `${categoryKey}-floor-${floor.name}`;
                
                // Floor row - inherits critical path from parent category
                result.push({
                  type: 'floor',
                  key: floorKey,
                  name: floor.name,
                  level: 2,
                  minDate: floor.minDate,
                  maxDate: floor.maxDate,
                  taskCount: floor.taskCount,
                  completed: floor.completed,
                  inProgress: floor.inProgress,
                  category: category.name,
                  dependsOnPrevFloor: floorIdx > 0,
                  isCriticalPath: isCriticalCategory
                });
              });
            }
          });
        }
      });

    return result;
  }, [groupedData, expandedGroups]);

  // Filter rows for critical path only view
  const displayedRows = useMemo(() => {
    if (!criticalPathOnly) return rows;
    
    // When filtering to critical path only, show:
    // - Building headers (always, for context)
    // - Category and floor rows that are on critical path
    return rows.filter(row => {
      if (row.type === 'building') return true; // Always show buildings for context
      return row.isCriticalPath;
    });
  }, [rows, criticalPathOnly]);

  // Count critical path items
  const criticalPathCount = useMemo(() => {
    return rows.filter(r => r.isCriticalPath).length;
  }, [rows]);

  // Initialize expansion state: collapse to stage-level on first load
  useEffect(() => {
    if (!initializedExpansion && Object.keys(groupedData).length > 0) {
      collapseToStageLevel();
      setInitializedExpansion(true);
    }
  }, [initializedExpansion, groupedData, collapseToStageLevel]);

  // Auto-scroll to today's date when timeline is ready
  useEffect(() => {
    if (!hasScrolledToToday && totalDays > 0 && rightPanelRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToToday();
        setHasScrolledToToday(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasScrolledToToday, totalDays, scrollToToday]);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks to Display</h3>
        <p className="text-gray-600">No tasks match the current filters.</p>
      </div>
    );
  }

  const getBarStyle = (rowMinDate: Date | null, rowMaxDate: Date | null) => {
    if (!rowMinDate || !rowMaxDate) return { display: 'none' as const };

    const daysFromStart = Math.ceil((rowMinDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, Math.ceil((rowMaxDate.getTime() - rowMinDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    const left = Math.max(0, (daysFromStart / totalDays) * 100);
    const width = (duration / totalDays) * 100;

    return { left: `${left}%`, width: `${Math.max(width, 1)}%` };
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const isExpanded = (key: string) => expandedGroups[key] !== false;

  const ROW_HEIGHT = 40;

  // Calculate min width based on zoom level
  const zoomConfig = ZOOM_CONFIG[zoomLevel];
  const timelineMinWidth = Math.max(timePeriods.length * zoomConfig.minColumnWidth, 600);

  return (
    <div className="p-4">
      {/* Header Controls */}
      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
          {/* Expand/Collapse Controls */}
          <div className="flex items-center gap-1 p-1.5 bg-gray-50 rounded-lg border border-gray-200">
            <button
              onClick={collapseToStageLevel}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              title="Collapse to Stage Level"
            >
              <Shrink className="w-3.5 h-3.5" />
              <span>Stages</span>
            </button>
            <button
              onClick={expandAll}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              title="Expand All Floors"
            >
              <Expand className="w-3.5 h-3.5" />
              <span>All</span>
            </button>
          </div>

          {/* Scroll to Today */}
          <button
            onClick={scrollToToday}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
            title="Scroll to Today"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Today</span>
          </button>

          {/* Critical Path Controls */}
          <div className="flex items-center gap-1 p-1.5 bg-gray-50 rounded-lg border border-gray-200">
            <button
              onClick={() => setShowCriticalPath(!showCriticalPath)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                showCriticalPath 
                  ? 'bg-red-100 text-red-700 border border-red-200' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title={showCriticalPath ? "Hide Critical Path Highlight" : "Show Critical Path"}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Critical</span>
              {showCriticalPath && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-800 rounded text-[10px]">
                  {criticalPathCount}
                </span>
              )}
            </button>
            {showCriticalPath && (
              <button
                onClick={() => setCriticalPathOnly(!criticalPathOnly)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  criticalPathOnly 
                    ? 'bg-red-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title={criticalPathOnly ? "Show All Tasks" : "Show Only Critical Path"}
              >
                {criticalPathOnly ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                <span>{criticalPathOnly ? 'All' : 'Only'}</span>
              </button>
            )}
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded-lg border border-gray-200">
            <ZoomOut className="w-4 h-4 text-gray-400" />
            <div className="inline-flex rounded-md bg-gray-100 p-0.5" role="group">
              {(['month', 'quarter', 'half-year', 'year'] as ZoomLevel[]).map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setZoomLevel(level)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${
                    zoomLevel === level
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {ZOOM_CONFIG[level].label}
                </button>
              ))}
            </div>
            <ZoomIn className="w-4 h-4 text-gray-400" />
          </div>
      </div>

      {/* Gantt Chart */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex">
          {/* Left Column - Structure Names */}
          <div className="w-80 border-r border-gray-200 bg-gray-50 flex-shrink-0 flex flex-col">
            <div className="p-3 font-semibold text-sm text-gray-700 border-b border-gray-200 bg-gray-100 flex items-center justify-between">
              <span>Building / Category / Floor</span>
              <span className="text-xs font-normal text-gray-500">Progress</span>
            </div>
            <div 
              ref={leftPanelRef}
              className="overflow-y-auto flex-1"
              style={{ maxHeight: CHART_BODY_HEIGHT, minHeight: '420px' }}
              onScroll={() => handleScroll('left')}
            >
              {displayedRows.map((row) => {
                const progress = row.taskCount > 0 ? Math.round((row.completed / row.taskCount) * 100) : 0;
                const canExpand = row.type !== 'floor';
                const isCritical = showCriticalPath && row.isCriticalPath;
                
                return (
                  <div
                    key={row.key}
                    className={`flex items-center border-b border-gray-200 ${canExpand ? 'cursor-pointer hover:bg-gray-100' : ''} ${
                      row.type === 'building' ? 'bg-gray-100 font-semibold' : ''
                    } ${isCritical ? 'bg-red-50 border-l-4 border-l-red-500' : ''}`}
                    style={{ 
                      height: `${ROW_HEIGHT}px`,
                      paddingLeft: `${row.level * 16 + 8}px`,
                      paddingRight: '8px'
                    }}
                    onClick={() => canExpand && toggleGroup(row.key)}
                  >
                    {/* Expand/Collapse icon */}
                    {canExpand ? (
                      isExpanded(row.key) 
                        ? <ChevronDown className="w-4 h-4 mr-1 text-gray-500 flex-shrink-0" />
                        : <ChevronRight className="w-4 h-4 mr-1 text-gray-500 flex-shrink-0" />
                    ) : (
                      <span className="w-5 flex-shrink-0" />
                    )}
                    
                    {/* Row label */}
                    {row.type === 'building' && (
                      <>
                        <Building2 className="w-3.5 h-3.5 mr-1 text-gray-500 dark:text-neutral-400 flex-shrink-0" />
                        <span className="text-sm truncate">{row.name}</span>
                      </>
                    )}
                    
                    {row.type === 'category' && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium text-white truncate ${getCategoryBarColor(row.name)}`}>
                        {row.name}
                      </span>
                    )}
                    
                    {row.type === 'floor' && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium truncate">
                        {row.name}
                      </span>
                    )}
                    
                    {/* Progress info */}
                    <div className="ml-auto flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-gray-500">
                        {row.completed}/{row.taskCount}
                      </span>
                      <span className="text-xs text-gray-400">
                        ({progress}%)
                      </span>
                      {(row.dependsOnRCC || row.dependsOnPrevFloor) && (
                        <GitBranch className="w-3 h-3 text-orange-500" title={row.dependsOnRCC ? 'Depends on RCC' : 'Depends on previous floor'} />
                      )}
                </div>
              </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Timeline */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Time Period Headers - synced with timeline horizontal scroll */}
            <div 
              ref={monthHeaderRef}
              className="border-b border-gray-200 bg-gray-100 flex-shrink-0 overflow-x-hidden"
            >
              <div className="flex" style={{ minWidth: `${timelineMinWidth}px` }}>
                {timePeriods.map((period, idx) => (
                <div
                  key={idx}
                    className="flex-shrink-0 text-center py-3 text-xs font-semibold text-gray-700 border-r border-gray-200"
                    style={{ width: `${(period.days / totalDays) * 100}%`, minWidth: `${zoomConfig.minColumnWidth}px` }}
                >
                    {period.label}
                </div>
              ))}
              </div>
            </div>

            {/* Timeline Bars */}
            <div 
              ref={rightPanelRef}
              className="overflow-y-auto overflow-x-auto flex-1"
              style={{ maxHeight: CHART_BODY_HEIGHT, minHeight: '420px' }}
              onScroll={() => handleScroll('right')}
            >
              <div className="relative" style={{ minWidth: `${timelineMinWidth}px` }}>
                {/* Today indicator line */}
                {(() => {
                  const today = new Date();
                  if (today >= minDate && today <= maxDate) {
                    const daysFromStart = Math.ceil((today.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
                    const leftPosition = (daysFromStart / totalDays) * 100;
                    return (
                      <div 
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                        style={{ left: `${leftPosition}%` }}
                      >
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded whitespace-nowrap">
                          Today
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                {displayedRows.map((row) => {
                  const barStyle = getBarStyle(row.minDate, row.maxDate);
                  const progress = row.taskCount > 0 ? (row.completed / row.taskCount) * 100 : 0;
                  const isCritical = showCriticalPath && row.isCriticalPath;
                  
                  // Different colors for different levels
                  let barColor = 'bg-slate-600';
                  if (row.type === 'category') {
                    barColor = getCategoryBarColor(row.name);
                  } else if (row.type === 'floor') {
                    barColor = getCategoryBarColor(row.category || '') + ' opacity-80';
                  }

                return (
                  <div
                      key={row.key}
                    className={`relative border-b border-gray-200 ${isCritical ? 'bg-red-50/30' : ''}`}
                      style={{ height: `${ROW_HEIGHT}px` }}
                  >
                      {/* Grid lines */}
                    <div className="absolute inset-0 flex">
                        {timePeriods.map((period, idx) => (
                        <div
                          key={idx}
                          className="flex-shrink-0 border-r border-gray-100"
                            style={{ width: `${(period.days / totalDays) * 100}%` }}
                        />
                      ))}
                    </div>

                      {/* Bar - Only show for non-header rows (category and floor) */}
                      {row.minDate && row.maxDate && !row.isHeader && (
                        <div
                          className="absolute top-1.5 cursor-pointer"
                          style={{ ...barStyle, height: `${ROW_HEIGHT - 12}px` }}
                          onMouseEnter={(e) => {
                            setHoverTip({
                              name: row.name,
                              type: row.type,
                              category: row.category,
                              minDate: row.minDate!,
                              maxDate: row.maxDate!,
                              taskCount: row.taskCount,
                              completed: row.completed,
                              progress,
                              dependsOnRCC: row.dependsOnRCC,
                              dependsOnPrevFloor: row.dependsOnPrevFloor,
                              isCritical,
                              x: e.clientX,
                              y: e.clientY,
                            });
                          }}
                          onMouseMove={(e) => {
                            setHoverTip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev);
                          }}
                          onMouseLeave={() => setHoverTip(null)}
                        >
                          <div className={`h-full ${barColor} rounded shadow-sm relative ${isCritical ? 'ring-2 ring-red-500 ring-offset-1' : ''}`}>
                            {/* Critical path indicator */}
                            {isCritical && (
                              <div className="absolute -left-0.5 top-0 bottom-0 w-1 bg-red-500 rounded-l" />
                            )}
                            {/* Progress fill */}
                            <div 
                              className="absolute inset-y-0 left-0 bg-green-400 opacity-50 rounded-l"
                              style={{ width: `${progress}%` }}
                            />
                            
                            {/* Label */}
                            <div className="absolute inset-0 flex items-center px-2 overflow-hidden">
                              <span className="text-xs font-medium text-white truncate drop-shadow-sm">
                                {row.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center flex-wrap gap-3 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-slate-600 rounded"></div>
          <span className="text-gray-600">Building</span>
        </div>
        {['RCC', 'Masonry', 'Plumbing', 'Electrical', 'Flooring & Tiling', 'Waterproofing'].map((name) => (
          <div key={name} className="flex items-center space-x-1">
            <div className={`w-3 h-3 ${getCategoryBarColor(name)} rounded`}></div>
            <span className="text-gray-600">{name}</span>
          </div>
        ))}
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-green-400 rounded opacity-50"></div>
          <span className="text-gray-600">Completed</span>
        </div>
        <div className="flex items-center space-x-1">
          <GitBranch className="w-3 h-3 text-orange-500" />
          <span className="text-gray-600">Dependency</span>
        </div>
      </div>

      {hoverTip && createPortal(
        (() => {
          const width = 260;
          const estHeight = 180;
          const pad = 10;
          let left = hoverTip.x + 14;
          let top = hoverTip.y + 14;
          if (left + width > window.innerWidth - pad) left = hoverTip.x - width - 14;
          if (top + estHeight > window.innerHeight - pad) top = hoverTip.y - estHeight - 14;
          if (left < pad) left = pad;
          if (top < pad) top = pad;
          return (
            <div
              className="hover-card-dark pointer-events-none fixed z-[200] w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-white/30"
              style={{ left, top }}
            >
              <div className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                {hoverTip.type === 'category' && <Layers className="w-3.5 h-3.5" />}
                {hoverTip.type === 'floor' && <Box className="w-3.5 h-3.5" />}
                {hoverTip.type === 'building' && <Building2 className="w-3.5 h-3.5" />}
                <span>{hoverTip.name}</span>
              </div>
              {hoverTip.type === 'floor' && hoverTip.category && (
                <div className="text-gray-400 mb-2">Category: {hoverTip.category}</div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-gray-400 text-[10px] uppercase">Start Date</div>
                  <div className="font-medium">{formatDate(hoverTip.minDate)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-[10px] uppercase">End Date</div>
                  <div className="font-medium">{formatDate(hoverTip.maxDate)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-[10px] uppercase">Tasks</div>
                  <div className="font-medium">{hoverTip.taskCount}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-[10px] uppercase">Progress</div>
                  <div className="font-medium">{hoverTip.completed}/{hoverTip.taskCount} ({hoverTip.progress.toFixed(0)}%)</div>
                </div>
              </div>
              {(hoverTip.dependsOnRCC || hoverTip.dependsOnPrevFloor || hoverTip.isCritical) && (
                <div className="mt-2 pt-2 border-t border-gray-700 text-[11px]">
                  {hoverTip.isCritical && <div className="text-red-400 font-medium mb-1">Critical Path — delays affect project end date</div>}
                  {hoverTip.dependsOnRCC && <div className="text-orange-400">Depends on RCC completion</div>}
                  {hoverTip.dependsOnPrevFloor && <div className="text-orange-400">Depends on previous floor</div>}
                </div>
              )}
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
};
